/**
 * Tap once for the bubble, again for the panel (build decisions §3).
 *
 * A wrapper around the host's four callbacks, not a change to them: the
 * callout's "Open panel", a deep link and the bubble's Details all still go
 * straight to the host, which stays the one way anything opens
 * (`MapSelectionHost`). A cleaning zone keeps its own immediate form.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { PlaceRegistry } from '../../../api/v1';
import type { MapDevice, MapDeviceKind } from '../../Map/map-bubble-anchor';
import type { MapSelectionHandlers, MapSelectionState } from '../../Map/MapSelectionHost';

const sameDevice = (a: { kind: string; id: string } | null, b: { kind: string; id: string } | null) =>
  a !== null && b !== null && a.kind === b.kind && a.id === b.id;

export interface MapBubbleModel {
  /** What the bubble is about, or null. */
  readonly device: MapDevice | null;
  /** The callbacks to hand a renderer in place of the host's. */
  readonly tap: MapSelectionHandlers;
  /** What the renderers call when a tap lands on nothing. */
  readonly close: () => void;
  /** Details ›: the panel, as the second tap opens it. */
  readonly openPanel: () => void;
}

export function useMapBubble(
  handlers: MapSelectionHandlers,
  selection: MapSelectionState,
): MapBubbleModel {
  const [device, setDevice] = useState<MapDevice | null>(null);
  const close = useCallback(() => setDevice(null), []);

  // The host's re-tap to clear compares with what was open when the PRESS
  // began, because a mouse's outside-press dismiss has closed the drawer by
  // pointer-up (`MapSelectionHost`). A tap on the device whose panel is open
  // is that re-tap and belongs to the host; a bubble there would turn "close
  // the panel" into "open a bubble".
  const selectedRef = useRef(selection.selected);
  selectedRef.current = selection.selected;
  const openAtPress = useRef<MapSelectionState['selected']>(null);
  useEffect(() => {
    const onPress = () => {
      openAtPress.current = selectedRef.current;
    };
    document.addEventListener('pointerdown', onPress, true);
    return () => document.removeEventListener('pointerdown', onPress, true);
  }, []);

  const select = (kind: MapDeviceKind, id: string) => {
    if (kind === 'robot') handlers.onSelectRobot(id);
    else if (kind === 'lift') handlers.onSelectLift(id);
    else handlers.onSelectDoor(id);
  };
  const tapDevice = (kind: MapDeviceKind, id: string) => {
    const next = { kind, id };
    if (sameDevice(openAtPress.current, next)) {
      setDevice(null);
      select(kind, id);
      return;
    }
    if (sameDevice(device, next)) {
      setDevice(null);
      select(kind, id);
      return;
    }
    // On touch a drawer is still up at this point (its dismiss listens for
    // the compatibility mousedown, after): one thing open at a time.
    if (selectedRef.current) selection.clear();
    setDevice(next);
  };

  const tap: MapSelectionHandlers = {
    onSelectRobot: (name) => tapDevice('robot', name),
    onSelectLift: (name) => tapDevice('lift', name),
    onSelectDoor: (name) => tapDevice('door', name),
    onSelectPlace: (place: PlaceRegistry) => {
      setDevice(null);
      handlers.onSelectPlace(place);
    },
  };

  const openPanel = () => {
    if (!device) return;
    setDevice(null);
    select(device.kind, device.id);
  };

  // Esc, while there is a bubble.
  const open = device !== null;
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDevice(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return useMemo(
    () => ({ device, tap, close, openPanel }),
    // `tap` and `openPanel` close over the device and the host's handlers,
    // which change every render; the model is read fresh each render anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [device, handlers, selection, close],
  );
}
